import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router";
import VideoBox from "../../casinoComponents/videoBox";
import CasinoHeading from "../../casinoComponents/casinoHeading";
import CasinoLastResult from "../../casinoComponents/casinoLastResult";
import FiveCricketMyBet from "./FiveCricketMyBet";
import BetTableSuperOver2 from "../../casinoComponents/betTableSuperOver2";
import FiveCricketCardsOverlay from "../../casinoComponents/videoBox/overlays/FiveCricketCardsOverlay";
import FiveCricketLayout from "./FiveCricketLayout";
import FiveCricketLastResults from "./FiveCricketLastResults";
import FiveCricketScorecard from "./FiveCricketScorecard";
import useFiveCricketScorecard from "./useFiveCricketScorecard";
import { getCasinoGameDetails, getMatchExposure, getCasinoOpenBets } from "../../../apiservices/CasionApi";
import ViewMoreModal from "../../../components/ViewMoreModal";
import { extractCasinoGame } from "../../../utils/casinoGame";
import { superOverSessionBet } from "../../../utils/casinoMyBets";
import "../../../styles/games/superover.css";

/**
 * Per-route reference differences (allpanel9 admin, casino/superover | superover2 | cricketv3):
 * .casino-table classes, score banner, video overlay (balls / cards / none), Last Result
 * placement (after .casino-table, or inside it for cricketv3), the rules card and result letters.
 */
const VARIANTS = {
  superover: {
    tableClass: "five-cricket super-over detail-page-container",
    banner: true,
    overlay: "balls",
    resultsInTable: false,
    cricketRule: true,
    labels: ["E", "R"],
  },
  superover2: {
    tableClass: "five-cricket super-over super-over2 detail-page-container",
    banner: false,
    overlay: null,
    resultsInTable: false,
    cricketRule: false,
    labels: ["I", "E"],
  },
  cricketv3: {
    tableClass: "five-cricket detail-page-container",
    banner: true,
    overlay: "cards",
    resultsInTable: true,
    cricketRule: false,
    labels: ["A", "I"],
  },
};

const RULE_ROWS = [
  { card: "cardA", ball: "ball1" },
  { card: "card2", ball: "ball2" },
  { card: "card3", ball: "ball3" },
  { card: "card4", ball: "ball4" },
  { card: "card6", ball: "ball6" },
  { card: "card10", ball: "ball0" },
  { card: "cardK", ball: "wicket", isWicket: true },
];
const SO_IMG = `${import.meta.env.BASE_URL}img/superOver`;

// Reference superover right sidebar: static "Inning's Card Rules" card under My Bets
function CricketRuleCard() {
  return (
    <div className="card mb-2 my-bet cricket-rule">
      <div className="card-header text-center">
        <span>
          ENG vs RSA
          <br />
          Inning&apos;s Card Rules
        </span>
      </div>{" "}
      <div className="card-body">
        <div className="card">
          <div className="card-header">
            <div className="row row5 mt-1">
              <div className="col-4">Cards</div> <div className="col-3 text-center">Count</div>{" "}
              <div className="col-5 text-right">Value</div>
            </div>
          </div>{" "}
          <div className="card-body">
            {RULE_ROWS.map((r) => (
              <div className="row row5 mt-1" key={r.card}>
                <div className="col-4">
                  <img src={`${SO_IMG}/cards/${r.card}.png`} alt="" /> <span className="ml-2">X</span>
                </div>{" "}
                <div className="col-3 text-center">5</div>{" "}
                <div className="col-5 text-right value">
                  {r.isWicket && " WICKET "}
                  <img src={`${SO_IMG}/balls/${r.ball}.png`} alt="" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function FiveCricketGame({ defaultName }) {
  const [gameData, setGameData] = useState(null);
  const [subData, setSubData] = useState([]);
  const [exposures, setExposures] = useState({});
  const [myBets, setMyBets] = useState([]);
  const [showViewMoreModal, setShowViewMoreModal] = useState(false);

  const { pathname } = useLocation();
  // Routes are matched case-insensitively (casino/superOver == casino/superover); the feed, VARIANTS
  // and titles are keyed by the lowercase reference game key.
  const gameName = (extractCasinoGame(pathname) || defaultName).toLowerCase();
  const gameType = gameName;
  const variant = VARIANTS[gameType] || VARIANTS.superover;
  const iframeSrc = `https://stream-s-43.uhdmovies.online/casino-stream?id=${gameName}`;
  const matchId =
    gameData?.data?.data?.t1?.gmid?.toString() ||
    gameData?.data?.data?.mid?.toString() ||
    gameData?.data?.mid?.toString() ||
    gameData?.mid?.toString();

  useEffect(() => {
    const name = (extractCasinoGame(pathname) || defaultName).toLowerCase();

    const fetchGameData = async () => {
      try {
        const response = await getCasinoGameDetails(name);
        setGameData(response);
        setSubData(response?.data?.data?.t2 || []);
      } catch (err) {
        console.error("Error fetching game data: ", err);
      }
    };

    fetchGameData();
    const interval = setInterval(fetchGameData, 1500);
    return () => clearInterval(interval);
  }, [pathname, defaultName]);

  const fetchExposure = useCallback(async () => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const userId = user?.user_id || user?.id || user?.staff_id || user?.owner_id;

      if (userId && matchId) {
        const response = await getMatchExposure(userId, matchId);
        if (response.success && response.data) {
          const exposureMap = {};
          response.data.forEach((item) => {
            const teamName = item.team_name || item.selection || item.player_name || "";
            const exposureAmount = parseFloat(item.exposure_amount) || 0;
            // Fancy session row: one runs-ladder market per row (game_type "<game>:session <row>",
            // server DYNAMIC_MARKETS.superover) — the row shows the panel's worst total.
            const ladderRow = String(item.game_type || "").match(/^[^:]+:session (.+)$/)?.[1].trim();
            if (ladderRow) {
              const worst = Math.min(exposureMap[ladderRow] ?? Infinity, exposureAmount);
              exposureMap[ladderRow] = worst;
              exposureMap[ladderRow.toLowerCase()] = worst;
              return;
            }
            if (teamName) {
              exposureMap[teamName] = exposureAmount;
              exposureMap[teamName.toLowerCase()] = exposureAmount;
              exposureMap[teamName.trim()] = exposureAmount;
              // 5Five Fancy rows carry their line ("Aus Over 3 45"): the row shows the sum of its lines under the bare nat.
              const base = gameType === "cricketv3" && !/(back|lay)$/i.test(teamName) && teamName.match(/^(.+?)\s+\d+(?:\.\d+)?$/)?.[1];
              if (base) {
                exposureMap[base] = (exposureMap[base] || 0) + exposureAmount;
                exposureMap[base.toLowerCase()] = exposureMap[base];
              }
            }
          });
          setExposures(exposureMap);
        }
      }
    } catch (error) {
      console.error("Error fetching exposure:", error);
    }
  }, [matchId, gameType]);

  const fetchMyBetsData = useCallback(async () => {
    const name = (extractCasinoGame(pathname) || defaultName).toLowerCase();
    if (!name) return;
    try {
      const response = await getCasinoOpenBets(name);
      if (response?.success && Array.isArray(response.data)) {
        const formattedBets = response.data.map((bet) => ({
          // My Bets "Gametype" column (casino_bets.game_name, e.g. mogambo)
          description: bet.description ?? bet.game_name ?? bet.gameName,
          userName: bet.username || bet.userName || "-",
          matchedBet: bet.matchedBet || bet.nation || bet.selection || bet.player_name || "-",
          selection: bet.selection || bet.player_name || bet.matchedBet || "",
          player_name: bet.player_name || "",
          odds: bet.odds ?? bet.userrate ?? "0",
          stake: bet.stake ?? bet.amount ?? "0",
          placeDate: bet.placeDate ?? bet.created_at ?? "-",
          gametype: bet.gametype ?? bet.game_type ?? (bet.type === "lay" ? "Lay" : bet.type === "back" ? "Back" : "-"),
          type: bet.type || null,
          exposer: parseFloat(bet.exposer || bet.exposure_amount || "0") || 0,
        }));
        setMyBets(formattedBets);
      }
    } catch (error) {
      console.error("Error fetching my bets (open-downline):", error);
    }
  }, [pathname, defaultName]);

  useEffect(() => {
    if (matchId) {
      const t = setTimeout(() => fetchExposure(), 2000);
      const exposureInterval = setInterval(fetchExposure, 2000);
      return () => {
        clearTimeout(t);
        clearInterval(exposureInterval);
      };
    }
  }, [fetchExposure, matchId]);

  useEffect(() => {
    const first = setTimeout(fetchMyBetsData, 0);
    const intervalId = setInterval(fetchMyBetsData, 3000);
    return () => {
      clearTimeout(first);
      clearInterval(intervalId);
    };
  }, [fetchMyBetsData]);

  const handleBetClick = () => {
    fetchExposure();
    fetchMyBetsData();
  };

  const t1 = gameData?.data?.data?.t1;
  const roundId = t1?.gmid || gameData?.data?.data?.mid || gameData?.data?.mid || gameData?.mid || "";
  const leftTime = t1?.lt ?? gameData?.data?.data?.lt;
  const scorecard = useFiveCricketScorecard(gameType, variant.banner);

  const lastResult = (
    <CasinoLastResult
      gameType={gameType}
      resultComponent={<FiveCricketLastResults gameType={gameType} labels={variant.labels} roundId={roundId} />}
    />
  );

  return (
    <FiveCricketLayout
      tableClass={variant.tableClass}
      // superover2 hides the flip clock while leftTime is 0 (reference v-show); see superover.css
      tableAttrs={{ "data-left-time": Number(leftTime) === 0 ? "0" : undefined }}
      betCount={myBets.length}
      afterTable={variant.resultsInTable ? null : lastResult}
      rightSide={
        <>
          <FiveCricketMyBet
            bets={myBets.map((b) => { const s = superOverSessionBet(b); return s ? { ...b, selection: s.nation, odds: s.rate } : b; })}
            onViewMore={() => setShowViewMoreModal(true)}
          />
          {variant.cricketRule && (
            <>
              {" "}
              <CricketRuleCard />
            </>
          )}
        </>
      }
    >
      <CasinoHeading name={t1?.ename || ""} roundId={roundId} />{" "}
      {variant.banner && (
        <>
          <div className="banner">
            <FiveCricketScorecard
              scorecard={scorecard}
              gmid={t1?.gmid}
              scard={t1?.scard}
              isFiveOver={gameType === "cricketv3"}
            />
          </div>{" "}
        </>
      )}
      <div>
        <VideoBox
          className="mt-0"
          iframeSrc={iframeSrc}
          timerValue={leftTime}
          gameType={gameType}
          cardsOverlay={variant.overlay ? <FiveCricketCardsOverlay card={t1?.card} kind={variant.overlay} /> : null}
        />
      </div>{" "}
      <BetTableSuperOver2 data={subData} onBetClick={handleBetClick} exposures={exposures} myBets={myBets} />
      {t1?.remark ? (
        <div className="casino-remark mt-3">
          <div className="remark-icon">
            <img src={`${import.meta.env.BASE_URL}img/icons/remark.png`} alt="" />
          </div>{" "}
          <marquee>{t1.remark}</marquee>
        </div>
      ) : null}
      {variant.resultsInTable && lastResult}
      <ViewMoreModal
        show={showViewMoreModal}
        onHide={() => setShowViewMoreModal(false)}
        title="View More"
        sectionKey={gameName}
        formatNation={(nat, bet) => superOverSessionBet(bet)?.nation ?? nat}
        formatRate={(bet) => superOverSessionBet(bet)?.rate ?? bet.odds ?? bet.userrate}
      />
    </FiveCricketLayout>
  );
}
